// Live's "Save to Library" image (spec G13b, PR E1; live's `flatten-layers.ts`): the visible layers
// bottom to top on a canvas of the document's size, each picture contain-fitted in its place and
// each text drawn as on the canvas, as a PNG named `layered-image-<time>.png`.
import { DownloadError, fetchSignedFile } from "../../../api/download";
import { LayerError } from "./errors";
import { fittedIn, placement, visible, type Composition } from "./model";
import { drawText, loadTextFont } from "./text";

/** Live's words. */
export const SAVED = "Layered image saved to Library";
export const NOT_SAVED = "Failed to save the image";
const NO_LAYER = "Add at least one visible layer before saving.";
const NO_EXPORT = "This browser cannot export the edited image.";
const NOT_EXPORTED = "The edited image could not be exported.";

/** A picture's pixels, drawable on a canvas. */
interface Bitmap {
  width: number;
  height: number;
  close: () => void;
}

/** How the image is made: a canvas, and a picture's pixels from its signed URL. A test's page
 * has neither, so a test replaces them. */
export const FLATTEN = {
  canvas: (): HTMLCanvasElement => document.createElement("canvas"),
  bitmap: async (url: string, signal: AbortSignal): Promise<Bitmap & CanvasImageSource> => {
    try {
      return await createImageBitmap(await fetchSignedFile(url, signal));
    } catch (error) {
      if (error instanceof DownloadError) {
        throw new LayerError(
          error.status === undefined ? "A layer image could not be loaded." : `A layer image could not be loaded (${error.status}).`,
        );
      }
      throw error;
    }
  },
};

/** Live's file name: the ISO time with its `:` and `.` as `-`. */
export function layeredName(now: Date): string {
  return `layered-image-${now.toISOString().replace(/[:.]/g, "-")}.png`;
}

/** The composition as a PNG of the document's size. */
export async function flatten(composition: Composition, signal: AbortSignal): Promise<Blob> {
  const shown = visible(composition);
  if (shown.length === 0) throw new LayerError(NO_LAYER);
  const canvas = FLATTEN.canvas();
  canvas.width = Math.max(1, Math.round(composition.width));
  canvas.height = Math.max(1, Math.round(composition.height));
  const context = canvas.getContext("2d");
  if (context === null) throw new LayerError(NO_EXPORT);
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = "high";
  // Every picture read and every font in before anything is drawn, as live's.
  const prepared = await Promise.allSettled(
    shown.map((id) => {
      const layer = composition.layers[id]!;
      return layer.kind === "picture" ? FLATTEN.bitmap(layer.url, signal) : loadTextFont(layer).then(() => null);
    }),
  );
  const bitmaps = prepared.map((result) => (result.status === "fulfilled" ? result.value : null));
  try {
    const failure = prepared.find((result) => result.status === "rejected");
    if (failure !== undefined) throw failure.reason;
    for (const [at, id] of shown.entries()) {
      const layer = composition.layers[id]!;
      const place = placement(composition, id);
      const bitmap = bitmaps[at];
      if (layer.kind === "text") drawText(context, layer, place);
      else if (bitmap !== null && bitmap !== undefined) {
        const frame = fittedIn(place, bitmap.width, bitmap.height);
        context.drawImage(bitmap, frame.x, frame.y, frame.width, frame.height);
      }
    }
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((blob) => (blob === null ? reject(new LayerError(NOT_EXPORTED)) : resolve(blob)), "image/png"),
    );
  } finally {
    for (const bitmap of bitmaps) bitmap?.close();
  }
}
